import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { CategoryDesktopFilter } from './CategoryDesktopFilter';
import * as S from './DesktopFilters.styles';
import { DESKTOP_FILTERS_CONTAINER_ID } from './DesktopFilters.constants';
import { DesktopFiltersHeader } from './DesktopFiltersHeader';
import { PriceDesktopFilter } from './PriceDesktopFilter';
import { RegionDesktopFilter } from './RegionDesktopFilter';
import { CollectionDesktopFilter } from './CollectionDesktopFilter';
import { RangeDesktopFilter } from './RangeDesktopFilter';
import { BooleanDesktopFilter } from './BooleanDesktopFilter';
import { SellerTypeDesktopFilter } from './SellerTypeDesktopFilter';
import { useParseQuery } from '@/hooks/useParseQuery/useParseQuery';
import { formatNumber } from '@/utils/helpers';
import { PRICE_INPUT_PRECISION } from '@/components/Filters/FiltersDrawer.constants';
import { AdOrder } from '@/api/types/ads.types';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { separateBooleanProperties } from '@/components/Filters/FiltersDrawer.helpers';
import {
  GetCategoryPropertiesResponse,
  PropertyType,
} from '@/api/types/categories.types';
import {
  mapBooleanPropertyOptionsToBooleanCollection,
  mapCollectionPropertyOptionsToCollection,
  mapRangePropertyOptionsToRangeCollection,
} from '@/hooks/useParseQuery/useParseQuery.helpers';
import { useServerDataContext } from '@/contexts';
import { GET_CATEGORY_PROPERTIES } from '@/api/queries/categories.queries';
import { GetRegionsResponse } from '@/api/types/regions.types';
import { GET_REGIONS } from '@/api/queries/regions.queries';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import { countActiveFilters } from '@/components/Filters/helpers';

const isBrowser = typeof window !== 'undefined';

export const DesktopFilters = () => {
  const {
    propertyOptions: {
      boolean: booleanPropertyOptions,
      range: rangePropertyOptions,
      collection: collectionPropertyOptions,
    },
    price: { from: priceFrom, to: priceTo },
    regionLegacyId,
    order,
    isShop,
  } = useParseQuery();
  const { currentCategory: serverCurrentCategory } = useServerDataContext();
  const { currentCategory = serverCurrentCategory, isAllCategories } =
    useCurrentCategory();
  const {
    data,
    previousData,
    loading: isCategoryPropertiesLoading,
  } = useQuery<GetCategoryPropertiesResponse>(GET_CATEGORY_PROPERTIES, {
    variables: {
      path: currentCategory?.path,
      collectionFilters: collectionPropertyOptions,
    },
    errorPolicy: 'all',
    skip: !isBrowser,
  });
  const { data: regions, loading: isRegionsLoading } =
    useQuery<GetRegionsResponse>(GET_REGIONS, { skip: !isBrowser });

  const categoryProperties = (data || previousData)?.category?.properties ?? [];
  const currentRegion =
    regions?.regions?.find(
      r => Number(r.legacyResourceId) === Number(regionLegacyId)
    ) ?? null;

  const {
    currentCollection,
    currentRangeCollection,
    currentBooleanCollection,
  } = useMemo(
    () => ({
      currentBooleanCollection: mapBooleanPropertyOptionsToBooleanCollection(
        booleanPropertyOptions
      ),
      currentRangeCollection:
        mapRangePropertyOptionsToRangeCollection(rangePropertyOptions),
      currentCollection: mapCollectionPropertyOptionsToCollection(
        collectionPropertyOptions
      ),
    }),
    [booleanPropertyOptions, rangePropertyOptions, collectionPropertyOptions]
  );

  const { booleanProperties, restProperties } =
    separateBooleanProperties(categoryProperties);

  const filtersCount = countActiveFilters({
    baseConditions: [
      !isAllCategories,
      priceFrom || priceTo,
      regionLegacyId,
      order && order !== AdOrder.DateDesc,
      isShop !== null,
    ],
    collectionValues: Object.values(collectionPropertyOptions),
    booleanValues: Object.values(booleanPropertyOptions),
    hasBooleanValue: () => true,
    rangeValues: Object.values(rangePropertyOptions),
    hasRangeValue: value => Boolean(value?.valueFrom || value?.valueTo),
  });

  const prices = {
    from: priceFrom
      ? formatNumber(priceFrom, PRICE_INPUT_PRECISION, true)
      : null,
    to: priceTo ? formatNumber(priceTo, PRICE_INPUT_PRECISION, true) : null,
  };

  const isResetButtonDisabled =
    (filtersCount === 1 && !isAllCategories) || !filtersCount;
  const hasCategoryPropertiesData = Boolean((data || previousData)?.category);
  const isLoading =
    !isBrowser ||
    (isCategoryPropertiesLoading && !hasCategoryPropertiesData) ||
    isRegionsLoading;

  return (
    <S.Container
      id={DESKTOP_FILTERS_CONTAINER_ID}
      data-testid={SearchResultsPageTestIds.desktopFilters.container}
    >
      <CategoryDesktopFilter isLoading={isLoading} />
      <S.FiltersContainer>
        <DesktopFiltersHeader
          isLoading={isLoading}
          isResetButtonDisabled={isResetButtonDisabled}
        />
        <PriceDesktopFilter currentPrices={prices} isLoading={isLoading} />
        <RegionDesktopFilter
          isLoading={isLoading}
          regions={regions?.regions}
          currentRegion={currentRegion}
        />
        {!!restProperties.length &&
          restProperties.map(p => {
            switch (p.type) {
              case PropertyType.CollectionProperty:
                return (
                  <CollectionDesktopFilter
                    key={p.legacyResourceId}
                    collection={currentCollection}
                    propertyLegacyId={p.legacyResourceId}
                    options={p.options}
                    popularOptions={p.popularOptions}
                    allProperties={categoryProperties}
                    filterOpenByDefault={p.filterOpenByDefault}
                  />
                );
              case PropertyType.RangeProperty:
                return (
                  <RangeDesktopFilter
                    key={p.legacyResourceId}
                    property={p}
                    collection={currentRangeCollection}
                  />
                );
              default:
                return null;
            }
          })}
        {booleanProperties.map(p => (
          <BooleanDesktopFilter
            key={p.legacyResourceId}
            property={p}
            collection={currentBooleanCollection}
          />
        ))}
        <SellerTypeDesktopFilter isShop={isShop} isLoading={isLoading} />
      </S.FiltersContainer>
    </S.Container>
  );
};
